import { Page } from "@components-common/layouts";
import { di } from "@dirootie-core/di";
import { Table } from "@components-defaults";
import { structuralLayerFunctionData } from "../../../components-domain/bim-domain";
import { ScreenConfig } from "@components-fabric";
import { RootieTable } from "../../../components-common/tables/RootieTable";
import { StructuralLayerInfo } from "../../../components-domain/bim-domain/StructuralLayerInfo";
import { Toolbar } from "@components-common/toolbar";
import { FILTER_TYPES } from "taltech-styleguide";

/**
 * Configuration Object: tableProps
 *
 * This object defines the properties for the RootieTable component.
 * It includes configuration for pagination, source type, and elements.
 *
 * @property {React.Component} component - The table component to render.
 * @property {Object} ...Table.Index - The index properties from the Table object.
 * @property {string} keyField - The key field for the table.
 * @property {undefined} pagination - Pagination configuration (undefined).
 * @property {boolean} showPagination - Flag to show pagination (false).
 * @property {boolean} showRowActions - Flag to show row actions (false).
 * @property {Object} source - The source configuration for the table data.
 * @property {Array} source.elements - The elements data for the table.
 * @property {string} source.type - The source type (MEMORY).
 */
const tableProps = {
  component: RootieTable,
  ...Table.Index,
  keyField: "type",
  pagination: undefined,
  showPagination: false,
  showRowActions: false,
  source: {
    elements: structuralLayerFunctionData,
    type: "MEMORY",
  },
};

/**
 * Configuration Object: structuralLayerFunctionsIndexScreen
 *
 * This object defines the configuration for the structural layer functions index screen.
 * It includes the screen ID, path, roles, template type, and component configuration.
 *
 * @property {string} id - The ID of the screen.
 * @property {string} path - The URL path for the screen.
 * @property {Array} roles - The roles that have access to this screen.
 * @property {string} tmp - The template type (custom).
 * @property {React.Component} Component - The component to render the page.
 * @property {Array} children - An array of child components to be rendered within the screen.
 */
export const structuralLayerFunctionsIndexScreen: ScreenConfig = {
  id: "structural_layer_functions",
  path: "structural-layer-functions",
  title: "Structural Layer Functions",
  roles: ["STUDY_DIRECTOR", "LECTURER", "STUDENT"],
  tmp: "custom",
  Component: Page,
  children: [
    {
      id: "toolbar_structural_layer",
      Component: Toolbar,
      props: {
        right: ["back"],
      },
    },
    {
      id: "tbl",
      Component: RootieTable,
      props: {
        ...tableProps,
        keyField: "hierarchy",
        columns: [
          {
            id: "custom",
            classes: "test-column",
            dataField: "hierarchy",
            text: "Hierarchy",
          },
          {
            id: "custom",
            classes: "test-column",
            dataField: "function",
            sort: true,
            filterObject: {
              defaultValue: undefined,
              minChars: 1,
              placeholder: "Function",
              type: FILTER_TYPES.TEXT,
            },
            text: "Function",
          },
          {
            id: "custom",
            classes: "test-column",
            dataField: "type",
            sort: true,
            filterObject: {
              defaultValue: undefined,
              minChars: 1,
              placeholder: "Type",
              type: FILTER_TYPES.TEXT,
            },
            text: "Type",
            formatter: (col: string = "") => di.str.startCase(col),
          },
        ],
      },
      type: "static",
    },
    {
      Component: StructuralLayerInfo,
    },
  ],
} as any;
